import type { DashboardProject } from '../../../types/project';

export const MANUAL_WELDING_MACHINE_ID = 'manual';

export type ManualWeldingMachineOption = {
  value: string;
  label: string;
};

/** Manual status configuration machine ids — must match API `WeldingSystemConfigEnum`. */
export const MANUAL_STATUS_WELDING_MACHINES: ManualWeldingMachineOption[] = [
  { value: 'p600z', label: 'P600Z' },
  { value: 'm500', label: 'M500' },
  { value: 'p625', label: 'P625' },
  { value: 'iwm', label: 'IWM' },
  { value: 'manual', label: 'Manual' },
];

const PROJECT_MACHINE_KEY_TO_API: Record<string, string> = {
  IWM: 'iwm',
  P625: 'p625',
  P600Z: 'p600z',
  M500: 'm500',
  Manual: 'manual',
};

const weldingHasEnabledMachine = (
  welding?: {
    enabled?: boolean;
    machines?: Record<string, { enabled?: boolean } | undefined>;
  } | null,
  machineKey?: string
): boolean => Boolean(welding?.enabled && machineKey && welding?.machines?.[machineKey]?.enabled);

const collectEnabledMachinesFromWelding = (
  welding?: {
    enabled?: boolean;
    machines?: Record<string, { enabled?: boolean } | undefined>;
  } | null,
  found: Set<string> = new Set()
): Set<string> => {
  Object.entries(PROJECT_MACHINE_KEY_TO_API).forEach(([machineKey, apiValue]) => {
    if (weldingHasEnabledMachine(welding, machineKey)) {
      found.add(apiValue);
    }
  });
  return found;
};

/** Project-enabled machines for the dropdown; falls back to all supported machines. */
export function getManualStatusWeldingMachineOptions(
  projectTypes?: DashboardProject['project']['projectTypes']
): ManualWeldingMachineOption[] {
  if (!projectTypes) {
    return MANUAL_STATUS_WELDING_MACHINES;
  }

  const enabled = new Set<string>();
  collectEnabledMachinesFromWelding(projectTypes.pipeline?.features?.welding, enabled);
  Object.values(projectTypes.fabrication?.options ?? {}).forEach((option) => {
    collectEnabledMachinesFromWelding(option?.features?.welding, enabled);
  });

  if (enabled.size === 0) {
    return MANUAL_STATUS_WELDING_MACHINES;
  }

  return MANUAL_STATUS_WELDING_MACHINES.filter((machine) => enabled.has(machine.value));
}

export function normalizeManualWeldingSystem(value?: string | null): string {
  const normalized = String(value ?? MANUAL_WELDING_MACHINE_ID)
    .trim()
    .toLowerCase()
    .replace(/\W/g, '');

  const aliasMap: Record<string, string> = {
    manual: 'manual',
    p600z: 'p600z',
    m500: 'm500',
    p625: 'p625',
    iwm: 'iwm',
  };

  return aliasMap[normalized] ?? MANUAL_WELDING_MACHINE_ID;
}

export function projectMachineKeyToApiValue(machineKey: string): string | null {
  return PROJECT_MACHINE_KEY_TO_API[machineKey] ?? null;
}

/** Display value for job dropdowns, e.g. `p600z: Demo`. */
export function formatManualJobSelectValue(machine: string, jobNumber: string): string {
  const job = jobNumber.trim();
  if (!job) return '';

  const colonIdx = job.indexOf(':');
  if (colonIdx > 0 && colonIdx < job.length - 1) {
    return `${normalizeManualWeldingSystem(job.slice(0, colonIdx))}: ${job.slice(colonIdx + 1).trim()}`;
  }

  const machineId = normalizeManualWeldingSystem(machine || MANUAL_WELDING_MACHINE_ID);
  return `${machineId}: ${job}`;
}

export function parseManualJobSelectValue(value: string): {
  machine: string;
  jobNumber: string;
} {
  const trimmed = value.trim();
  if (!trimmed) {
    return { machine: MANUAL_WELDING_MACHINE_ID, jobNumber: '' };
  }

  const colonIdx = trimmed.indexOf(':');
  if (colonIdx === -1) {
    return { machine: MANUAL_WELDING_MACHINE_ID, jobNumber: trimmed };
  }

  return {
    machine:
      normalizeManualWeldingSystem(trimmed.slice(0, colonIdx)) || MANUAL_WELDING_MACHINE_ID,
    jobNumber: trimmed.slice(colonIdx + 1).trim(),
  };
}

export function manualJobNumbersMatch(a: string, b: string): boolean {
  const left = parseManualJobSelectValue(a);
  const right = parseManualJobSelectValue(b);
  return (
    left.machine === right.machine &&
    left.jobNumber.trim().toLowerCase() === right.jobNumber.trim().toLowerCase()
  );
}

/** @deprecated Use getManualStatusWeldingMachineOptions */
export function getEnabledManualWeldingMachines(
  projectTypes?: DashboardProject['project']['projectTypes']
): ManualWeldingMachineOption[] {
  return getManualStatusWeldingMachineOptions(projectTypes);
}
